import React, { useMemo, useState, useEffect } from "react";
import { LogOut, Menu, PanelLeftOpen, X } from "lucide-react";
import Sidebar from "./Sidebar";
import ListaPedidos from "./ListaPedidos";
import GestionColegios from "./GestionColegios";
import GestionSalas from "./GestionSalas";
import GestionEquipos from "./GestionEquipos";
import GestionDocentes from "./GestionDocentes";
import GestionAdministradores from "./GestionAdministradores";
import GestionComponentes from "./GestionComponentes";
import GestionPermisosTecnicos from "./GestionPermisosTecnicos";
import GestionDetallesTecnico from "./GestionDetallesTecnico";
import GestionInformes from "./GestionInformes";
import GestionSolicitudesRepuestos from "./GestionSolicitudesRepuestos";
import CentroNotificaciones from "./CentroNotificaciones";
import "./VistaAdminLayout.css";

const esViewportMovil = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export default function VistaAdminLayout({
  vistaActiva,
  setVistaActiva,
  cerrarSesion,
  modo = "admin", // "admin" | "tecnico"
}) {
  const rolAdmin = useMemo(() => localStorage.getItem("rolAdmin") || "", []);
  const esAdminReal = useMemo(() => (rolAdmin || "").toLowerCase() === "admin", [rolAdmin]);

  // Tabs internos del técnico
  const [vistaTecnico, setVistaTecnico] = useState("pedidos"); // "pedidos" | "detalles"

  useEffect(() => {
    if (modo !== "tecnico") setVistaTecnico("pedidos");
  }, [modo]);

  const titulo = useMemo(() => {
    if (modo === "tecnico") return "Panel Técnico";
    return esAdminReal ? "Panel Administrador" : "Panel Técnico";
  }, [modo, esAdminReal]);

  const subtitulo = useMemo(() => {
    if (modo === "tecnico") return "Gestión de pedidos y detalles autorizados";
    return "Gestión completa del sistema";
  }, [modo]);

  const vistaMeta = useMemo(() => {
    const vistas = {
      "gestion-admins": ["Administradores", "Usuarios con acceso administrativo y técnico"],
      "gestion-docentes": ["Docentes", "Registro, edición y asociaciones"],
      "lista-pedidos": ["Pedidos", "Seguimiento y cambios de estado"],
      colegios: ["Colegios", "Instituciones registradas"],
      salas: ["Salas", "Espacios y laboratorios por colegio"],
      equipos: ["Equipos", "Inventario de equipos por sala"],
      componentes: ["Componentes", "Stock para informática y robótica"],
      "solicitudes-repuestos": ["Solicitudes de repuestos", "Aprobación y débito de stock"],
      "permisos-tecnicos": ["Permisos Técnicos", "Accesos de técnicos por sala"],
      informes: ["Informes", "Reportes y exportaciones"],
    };

    return vistas[vistaActiva] || ["Panel", "Gestión del sistema"];
  }, [vistaActiva]);

  const VistaSinPermiso = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-red-700 mb-2">Necesitas permiso del Administrador</h2>
      <p className="text-gray-700">Esta sección está disponible solo para administradores.</p>
    </div>
  );

  // ✅ Único control del menú (mobile + desktop)
  const [menuAbierto, setMenuAbierto] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth > 768;
  });

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuAbierto(false);
    };
    if (menuAbierto) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuAbierto]);

  useEffect(() => {
    if (!menuAbierto || !esViewportMovil()) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuAbierto]);

  // ✅ Al seleccionar opción: cambia vista y CIERRA menú (mobile/desktop)
  const seleccionarVista = (id) => {
    setVistaActiva(id);
    if (esViewportMovil()) setMenuAbierto(false);
  };

  return (
    <div className="admin-fondo">
      <header className="admin-header">
        <div className="admin-brand">
          <div className="admin-logo">FB</div>
          <div>
            <h1 className="admin-titulo">FBDesk</h1>
            <p className="admin-subtitulo">
              {titulo} · {subtitulo}
            </p>
          </div>
        </div>

        <div className="admin-actions">
          <CentroNotificaciones modo={modo} />

          <div className="admin-badge">
            {modo === "tecnico" ? "Técnico" : esAdminReal ? "Administrador" : "Técnico"}
          </div>

          <button className="admin-btn-salir" onClick={cerrarSesion}>
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </div>
      </header>

      {modo === "admin" ? (
        <>
          {/* ✅ Botón menú SOLO mobile */}
          <div className="admin-mobile-menubar">
            <button
              className="admin-mobile-menubtn"
              onClick={() => setMenuAbierto(true)}
              title="Abrir menú"
            >
              <Menu size={18} />
              Menú
            </button>
          </div>

          <div className="admin-contenedor">
            <div
              className="admin-hover-zone"
              onMouseEnter={() => {
                if (!esViewportMovil()) setMenuAbierto(true);
              }}
              aria-hidden="true"
            />

            {/* ✅ Sidebar DESKTOP colapsable */}
            <aside
              className={`admin-sidebar admin-sidebar-desktop ${menuAbierto ? "open" : ""}`}
              onMouseEnter={() => {
                if (!esViewportMovil()) setMenuAbierto(true);
              }}
            >
              <div className="admin-sidebar-top">
                <span className="admin-sidebar-title">Menú</span>
                <button
                  className="admin-sidebar-close"
                  onClick={() => setMenuAbierto(false)}
                  title="Cerrar menú"
                >
                  <X size={18} />
                </button>
              </div>

              <Sidebar onSeleccionar={seleccionarVista} opcionActiva={vistaActiva} />
            </aside>

            {/* ✅ Drawer MOBILE */}
            {menuAbierto && (
              <div className="admin-drawer-overlay" onClick={() => setMenuAbierto(false)}>
                <div className="admin-drawer" onClick={(e) => e.stopPropagation()}>
                  <div className="admin-drawer-top">
                    <span className="admin-sidebar-title">Menú</span>
                    <button
                      className="admin-sidebar-close"
                      onClick={() => setMenuAbierto(false)}
                      title="Cerrar"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <Sidebar onSeleccionar={seleccionarVista} opcionActiva={vistaActiva} />
                </div>
              </div>
            )}

            <main className="admin-main">
              <div className="admin-card anim-entrada">
                <div className="admin-viewbar">
                  <div>
                    <span className="admin-view-eyebrow">Vista actual</span>
                    <h2 className="admin-view-title">{vistaMeta[0]}</h2>
                    <p className="admin-view-subtitle">{vistaMeta[1]}</p>
                  </div>

                  <button
                    className="admin-desktop-menubtn"
                    onClick={() => setMenuAbierto((actual) => !actual)}
                    title={menuAbierto ? "Ocultar menú" : "Abrir menú"}
                  >
                    <PanelLeftOpen size={18} />
                    {menuAbierto ? "Ocultar menú" : "Abrir menú"}
                  </button>
                </div>

                {vistaActiva === "gestion-admins" && <GestionAdministradores />}
                {vistaActiva === "gestion-docentes" && <GestionDocentes />}
                {vistaActiva === "lista-pedidos" && <ListaPedidos />}
                {vistaActiva === "colegios" && <GestionColegios />}
                {vistaActiva === "salas" && <GestionSalas />}
                {vistaActiva === "equipos" && <GestionEquipos />}
                {vistaActiva === "componentes" && <GestionComponentes />}
                {vistaActiva === "solicitudes-repuestos" &&
                  (esAdminReal ? <GestionSolicitudesRepuestos /> : <VistaSinPermiso />)}

                {vistaActiva === "permisos-tecnicos" &&
                  (esAdminReal ? <GestionPermisosTecnicos /> : <VistaSinPermiso />)}

                {vistaActiva === "informes" &&
                  (esAdminReal ? <GestionInformes /> : <VistaSinPermiso />)}
              </div>
            </main>
          </div>
        </>
      ) : (
        // MODO TÉCNICO
        <main className="admin-main-tecnico">
          <div className="admin-card anim-entrada">
            <div className="admin-tabs">
              <button
                onClick={() => setVistaTecnico("pedidos")}
                className={`admin-tabbtn ${vistaTecnico === "pedidos" ? "active" : ""}`}
              >
                Pedidos
              </button>

              <button
                onClick={() => setVistaTecnico("detalles")}
                className={`admin-tabbtn ${vistaTecnico === "detalles" ? "active" : ""}`}
              >
                Detalles (Salas/Equipos)
              </button>
            </div>

            {vistaTecnico === "pedidos" ? (
              <>
                <h2 className="admin-h2">Pedidos</h2>
                <p className="admin-p">
                  Revisá los pedidos, filtrá y marcá como resueltos cuando corresponda.
                </p>
                <div className="admin-sep" />
                <ListaPedidos />
              </>
            ) : (
              <>
                <h2 className="admin-h2">Detalles autorizados</h2>
                <p className="admin-p">
                  Solo podrás editar detalles de salas/equipos si el Administrador te autorizó.
                </p>
                <div className="admin-sep" />
                <GestionDetallesTecnico />
              </>
            )}
          </div>
        </main>
      )}

      <footer className="admin-footer">
        <span>FBDesk</span> · Panel de administración y soporte
      </footer>
    </div>
  );
}
